import { Controller } from "@hotwired/stimulus";

export default class extends Controller<HTMLElement> {
    static targets = ["value", "trigger", "label", "panel", "calendar", "time"];
    declare readonly valueTarget: HTMLInputElement;
    declare readonly triggerTarget: HTMLButtonElement;
    declare readonly labelTarget: HTMLElement;
    declare readonly panelTarget: HTMLElement;
    declare readonly calendarTarget: HTMLElement & { value: string; focusedDate: string };
    declare readonly timeTarget: HTMLInputElement;
    private positionFrame?: number;

    connect(): void { this.renderLabel(); }
    disconnect(): void { this.stopPositioning(); }

    async edit(event: Event): Promise<void> {
        event.preventDefault();
        event.stopPropagation();
        const moduleUrl = "/static/calendar-elements.js";
        await import(moduleUrl);
        if (!this.element.isConnected) return;
        if (this.panelTarget.matches(":popover-open")) { this.panelTarget.hidePopover(); return; }
        const date = this.valueTarget.value ? new Date(this.valueTarget.value) : new Date();
        if (!this.valueTarget.value) { date.setDate(date.getDate() + 1); date.setHours(9, 0, 0, 0); }
        const local = this.localDate(date);
        this.calendarTarget.setAttribute("locale", this.locale);
        this.calendarTarget.setAttribute("first-day-of-week", "1");
        this.calendarTarget.setAttribute("min", this.localDate(new Date()).slice(0, 10));
        this.calendarTarget.setAttribute("max", this.localDate(new Date(Date.now() + 365 * 86400000)).slice(0, 10));
        this.calendarTarget.value = this.valueTarget.value ? local.slice(0, 10) : "";
        this.calendarTarget.focusedDate = local.slice(0, 10);
        this.timeTarget.value = local.slice(11, 16);
        this.panelTarget.setAttribute("popover", "manual");
        this.panelTarget.hidden = false;
        this.panelTarget.showPopover();
        this.position();
    }

    toggle(): void {
        const open = this.panelTarget.matches(":popover-open");
        this.triggerTarget.setAttribute("aria-expanded", String(open));
        this.panelTarget.hidden = !open;
        if (open) {
            this.positionFrame = requestAnimationFrame(this.trackPosition);
            document.addEventListener("pointerdown", this.dismissOutside);
            document.addEventListener("keydown", this.dismissEscape);
        } else this.stopPositioning();
    }

    confirmTime(event: KeyboardEvent): void {
        if (event.key !== "Enter" || event.isComposing) return;
        event.preventDefault();
        event.stopPropagation();
        this.selectDate(event);
        if (!this.valueTarget.value) return;
        this.timeTarget.blur();
        this.panelTarget.hidePopover();
    }

    selectDate(event: Event): void {
        event.stopPropagation();
        this.timeTarget.setCustomValidity("");
        if (!this.calendarTarget.value || !this.timeTarget.reportValidity()) { this.clearSelection(); return; }
        const local = `${this.calendarTarget.value}T${this.timeTarget.value}`;
        const date = new Date(local);
        const valid = Number.isFinite(date.getTime()) && date.getTime() > Date.now() && date.getTime() <= Date.now() + 365 * 86400000 && this.localDate(date) === local;
        this.timeTarget.setCustomValidity(valid ? "" : (this.locale === "fr-FR" ? "Choisissez une date et une heure dans les 365 prochains jours." : "Choose a date and time within the next 365 days."));
        if (!valid) { this.clearSelection(); this.timeTarget.reportValidity(); return; }
        this.valueTarget.value = date.toISOString();
        this.valueTarget.dispatchEvent(new Event("input", { bubbles: true }));
        this.renderLabel();
    }

    private clearSelection(): void {
        this.valueTarget.value = "";
        this.valueTarget.dispatchEvent(new Event("input", { bubbles: true }));
        this.labelTarget.textContent = this.locale === "fr-FR" ? "Choisir une date" : "Choose a date";
    }

    private renderLabel(): void {
        const date = new Date(this.valueTarget.value);
        if (Number.isFinite(date.getTime())) {
            this.labelTarget.textContent = new Intl.DateTimeFormat(this.locale, { dateStyle: "medium", timeStyle: "short" }).format(date);
        }
    }

    private trackPosition = (): void => {
        if (!this.panelTarget.matches(":popover-open")) return;
        this.position();
        // Timeline animations can move the anchor without resizing or scrolling it.
        this.positionFrame = requestAnimationFrame(this.trackPosition);
    };

    private position = (): void => {
        if (!this.panelTarget.matches(":popover-open")) return;
        const anchor = this.triggerTarget.getBoundingClientRect();
        const panel = this.panelTarget.getBoundingClientRect();
        const left = Math.max(8, Math.min(anchor.right - panel.width, window.innerWidth - panel.width - 8));
        const above = Math.max(0, anchor.top - 16);
        const below = Math.max(0, window.innerHeight - anchor.bottom - 16);
        const useBelow = this.panelTarget.scrollHeight <= below || below >= above;
        const available = useBelow ? below : above;
        this.panelTarget.style.maxHeight = `${available}px`;
        const height = this.panelTarget.getBoundingClientRect().height;
        const top = useBelow ? anchor.bottom + 8 : anchor.top - height - 8;
        Object.assign(this.panelTarget.style, { left: `${left}px`, top: `${top}px` });
    };

    private dismissOutside = (event: PointerEvent): void => {
        if (event.target instanceof Node && !this.panelTarget.contains(event.target) && !this.triggerTarget.contains(event.target)) this.panelTarget.hidePopover();
    };

    private dismissEscape = (event: KeyboardEvent): void => {
        if (event.key !== "Escape") return;
        event.preventDefault();
        this.panelTarget.hidePopover();
        this.triggerTarget.focus({ preventScroll: true });
    };

    private stopPositioning(): void {
        if (this.positionFrame !== undefined) cancelAnimationFrame(this.positionFrame);
        this.positionFrame = undefined;
        document.removeEventListener("pointerdown", this.dismissOutside);
        document.removeEventListener("keydown", this.dismissEscape);
    }
    private get locale(): string { return this.calendarTarget.dataset.locale ?? "fr-FR"; }
    private localDate(date: Date): string {
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    }
}
